// src/components/Chat/MessengerWidget.jsx
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useSocketManager } from "../SocketManager/SocketManager";
import ChatUI from "../ChatUI/ChatUI";
import UsersList from "../ChatUI/UsersList";
import {
  FiMessageCircle,
  FiX,
  FiWifi,
  FiWifiOff,
  FiAlertCircle,
  FiRefreshCw,
} from "react-icons/fi";

// Config
const ERP_API_URL = "https://api.digitrand.com/ERP/api";

const MessengerWidget = ({ employeeId, mobileMode }) => {
  const [isOpen, setIsOpen] = useState(mobileMode ? true : false);
  const [activeUserId, setActiveUserId] = useState(null);
  const [search, setSearch] = useState("");
  const [showConnectionError, setShowConnectionError] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const isMobileLayout = mobileMode === true;

  // Add this function inside MessengerWidget component, before the handleOpenConversation function:
  const createEmptyConversation = useCallback(
    () => ({
      messages: [],
      typing: false,
      unread: 0,
      page: 1,
      hasMore: true,
      loaded: false,
    }),
    []
  );

  // Get current user data
  useEffect(() => {
    if (!employeeId) return;

    const fetchCurrentUser = async () => {
      try {
        setLocalLoading(true);
        const response = await fetch(`${ERP_API_URL}/employees/${employeeId}`);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        if (data && data.id) {
          setCurrentUser({
            id: data.id,
            name: data.name || `Employee ${data.id}`,
            email: data.email || "",
            photo: data.photo?.trim() || null,
            department: data.department || "",
            designation: data.designation || "",
            employee_code: data.employee_code || "",
            status: data.status || "active",
          });
        } else {
          throw new Error("Invalid user data");
        }
      } catch (error) {
        console.error("❌ Error fetching current user:", error);
        setCurrentUser({
          id: employeeId,
          name: `Employee ${employeeId}`,
          email: "",
          photo: null,
          department: "",
          designation: "",
          employee_code: "",
          status: "active",
        });
      } finally {
        setLocalLoading(false);
      }
    };

    fetchCurrentUser();
  }, [employeeId]);

  // Initialize socket manager with currentUser object
  const socketManager = useSocketManager({
    currentUser,
    activeUserId,
  });

  const {
    employees,
    setEmployees,
    onlineIds,
    conversations,
    setConversations,
    loadingEmployees,
    socketConnected,
    connectionError,
    uploadingFiles,
    uploadProgress,
    sendMessage,
    handleTyping,
    handleFileUpload,
    updateMessageStatus,
    loadHistory,
    getOnlineUsers,
    markAllAsRead,
    clearChat,
    reconnect,
  } = socketManager;

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Calculate total unread messages
  const totalUnread = useMemo(() => {
    return Object.values(conversations).reduce(
      (sum, c) => sum + (c.unread || 0),
      0
    );
  }, [conversations]);

  // Find active user
  const activeUser = useMemo(() => {
    return employees.find((e) => e.id === activeUserId) || null;
  }, [employees, activeUserId]);

  // Inside MessengerWidget component, update handleOpenConversation:

  const handleOpenConversation = useCallback(
    (userId) => {
      console.log("📱 Opening conversation for user:", userId);
      setActiveUserId(userId);
      setIsOpen(true);

      // ✅ Load history via WebSocket
      loadHistory(userId, 1);

      // ✅ Set conversation structure
      setConversations((prev) => {
        const conv = prev[userId] || createEmptyConversation();
        return {
          ...prev,
          [userId]: {
            ...conv,
            unread: 0,
            loaded: false,
            isLoading: true, // Show loading indicator
          },
        };
      });

      // Mark as read
      markAllAsRead(userId);
    },
    [loadHistory, markAllAsRead, setConversations, createEmptyConversation]
  );

  const handleCloseConversation = () => {
    setActiveUserId(null);
  };

  // Handle scroll for infinite loading
  const handleScroll = (e) => {
    const el = e.target;
    if (!activeUserId || !el) return;

    if (el.scrollTop < 50) {
      const conv = conversations[activeUserId];
      if (conv && conv.hasMore) {
        loadHistory(activeUserId, (conv.page || 1) + 1);
      }
    }
  };

  // Fetch all employees (for contacts list)
  useEffect(() => {
    if (!employeeId) return;

    const fetchEmployees = async () => {
      try {
        setLocalLoading(true);
        const response = await fetch(`${ERP_API_URL}/employees`);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        if (Array.isArray(data)) {
          // Filter out current user and inactive employees
          const activeEmployees = data
            .filter((e) => e.status === "active" && e.id !== employeeId)
            .map((e) => ({
              id: e.id,
              name: e.name || `Employee ${e.id}`,
              email: e.email || "",
              photo: e.photo?.trim() || null,
              department: e.department || "",
              designation: e.designation || "",
              employee_code: e.employee_code || "",
              status: e.status || "active",
            }));

          setEmployees(activeEmployees);
          setAllEmployees(activeEmployees);
        }
      } catch (error) {
        console.error("❌ Error fetching employees:", error);
      } finally {
        setLocalLoading(false);
      }
    };

    fetchEmployees();
  }, [employeeId, setEmployees]);

  // Get online users when socket connects and panel opens
  useEffect(() => {
    if (isOpen && socketConnected) {
      getOnlineUsers();

      // Refresh online users every 30 seconds
      const interval = setInterval(() => {
        if (socketConnected) {
          getOnlineUsers();
        }
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isOpen, socketConnected, getOnlineUsers]);

  // Show connection error toast
  useEffect(() => {
    if (connectionError) {
      console.error("🔴 Connection Error:", connectionError);
      setShowConnectionError(true);

      const timer = setTimeout(() => {
        setShowConnectionError(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [connectionError]);

  const handleRetryConnection = () => {
    setShowConnectionError(false);
    reconnect();
  };

  const handleFileUploadWrapper = (userId, file) => {
    if (!socketConnected) {
      console.error("Cannot upload file: Socket not connected");
      return;
    }
    handleFileUpload(userId, file);
  };

  const isLoading = loadingEmployees || localLoading || !currentUser;

  // Show loading state
  if (isLoading && !isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-[10000]">
        <button
          className="relative flex items-center justify-center rounded-full shadow-2xl w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-400 to-gray-700 text-white"
          disabled
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </button>
      </div>
    );
  }

  // if (!currentUser) {
  //   return (
  //     <div className="fixed bottom-4 right-4 z-[10000]">
  //       <button
  //         className="relative flex items-center justify-center rounded-full shadow-2xl w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-800 text-white"
  //         onClick={() => alert("Unable to load user data. Please refresh.")}
  //       >
  //         <FiAlertCircle className="text-2xl" />
  //       </button>
  //     </div>
  //   );
  // }

  return (
    <>
      {/* Floating Bubble */}
      {!mobileMode && (
        <div className="fixed bottom-4 right-4 z-[10000]">
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className="relative flex items-center justify-center rounded-full shadow-2xl w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-950 text-yellow-400 hover:scale-110 active:scale-95 transition-all duration-200 border-[3px] border-yellow-300"
          >
            <span className="absolute inset-[-8px] rounded-full p-[3px] bg-gradient-to-tr from-yellow-400 via-yellow-200 via-orange-400 to-red-500 animate-rotateSmooth"></span>
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-blue-950"></span>
            <FiMessageCircle className="relative z-10 text-xl sm:text-2xl font-bold" />

            {!connectionError && totalUnread > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-black rounded-full min-w-6 h-6 flex items-center justify-center px-1.5 animate-pulse shadow-lg border-2 border-white z-20">
                {totalUnread > 9 ? "9+" : totalUnread}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Connection Error Toast */}
      {showConnectionError && (
        <div className="fixed top-4 right-4 z-[10001] animate-fade-in">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg shadow-lg max-w-sm">
            <div className="flex items-center">
              <FiAlertCircle className="text-red-500 text-xl mr-3" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">
                  Connection Error
                </p>
                <p className="text-xs text-red-600 mt-1">{connectionError}</p>
              </div>
              <button
                onClick={handleRetryConnection}
                className="ml-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-1 px-2 rounded transition-colors flex items-center gap-1"
              >
                <FiRefreshCw className="text-xs" />
                Retry
              </button>
              <button
                onClick={() => setShowConnectionError(false)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                <FiX className="text-sm" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Status Indicator */}
      {!socketConnected && isOpen && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10001] animate-fade-in">
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded-r-lg shadow-lg">
            <div className="flex items-center">
              <FiWifiOff className="text-yellow-500 text-lg mr-2" />
              <p className="text-sm font-semibold text-yellow-800">
                Connecting to server...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Messenger Panel */}
      {isOpen && (
        <div
          className={
            mobileMode
              ? "fixed inset-0 z-[9999] bg-white flex flex-col" // FULL SCREEN MODE
              : "fixed bottom-24 right-4 z-[9999] max-w-full sm:w-[700px] md:w-[800px] lg:w-[900px] h-[600px]"
          }
        >
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-yellow-400 flex flex-col sm:flex-row h-full overflow-hidden">
            {/* Left: Users List */}
            <div
              className={`w-full sm:w-[35%] md:w-[30%] flex flex-col bg-gradient-to-b from-blue-100 to-blue-50 border-r-2 border-yellow-300 ${
                !activeUser ? "flex" : "hidden sm:flex"
              }`}
            >
              <UsersList
                currentUser={currentUser}
                employees={employees}
                onlineIds={onlineIds}
                conversations={conversations}
                search={search}
                setSearch={setSearch}
                loadingEmployees={isLoading}
                socketConnected={socketConnected}
                onSelectUser={handleOpenConversation}
                activeUserId={activeUserId}
                onClose={() => setIsOpen(false)}
                mobileMode={mobileMode}
              />
            </div>

            {/* Right: Chat Window */}
            <div
              className={`${
                activeUser ? "h-full" : "flex-1"
              } w-full sm:w-[65%] md:w-[70%] flex flex-col bg-gradient-to-b from-blue-50 via-white to-blue-50`}
            >
              {!activeUser ? (
                !mobileMode && (
                  <div className="flex-1 flex flex-col items-center justify-center p-8">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-900 to-blue-950 flex items-center justify-center mb-4 shadow-lg">
                      <FiMessageCircle className="text-4xl text-yellow-400" />
                    </div>
                    <h3 className="text-lg font-bold text-blue-900 mb-2 text-center">
                      DigiTrand Messenger
                    </h3>
                    <p className="text-sm text-blue-700 text-center max-w-sm font-semibold mb-4">
                      Select a conversation to start messaging
                    </p>

                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          socketConnected
                            ? "bg-green-500 animate-pulse"
                            : "bg-red-500"
                        }`}
                      ></div>
                      <span className="text-sm font-medium text-gray-700">
                        {socketConnected
                          ? "Connected to server"
                          : "Disconnected"}
                      </span>
                    </div>

                    {!socketConnected && (
                      <div className="mt-2">
                        <button
                          onClick={reconnect}
                          className="flex items-center gap-2 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-2 px-3 rounded transition-colors"
                        >
                          <FiRefreshCw className="text-xs" />
                          Reconnect
                        </button>
                      </div>
                    )}

                    {connectionError && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg max-w-xs">
                        <p className="text-xs text-red-700 font-medium">
                          <FiAlertCircle className="inline mr-1" />
                          {connectionError}
                        </p>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <ChatUI
                  activeUser={activeUser}
                  currentUser={currentUser}
                  conversations={conversations}
                  onlineIds={onlineIds}
                  socketConnected={socketConnected}
                  connectionError={connectionError}
                  uploadingFiles={uploadingFiles}
                  uploadProgress={uploadProgress}
                  fileInputRef={fileInputRef}
                  messagesEndRef={messagesEndRef}
                  scrollContainerRef={scrollContainerRef}
                  onSendMessage={(text) => {
                    if (socketConnected) {
                      sendMessage(activeUser.id, text);
                    } else {
                      console.error("Cannot send: Socket not connected");
                    }
                  }}
                  onTyping={(typing) => {
                    if (socketConnected) {
                      handleTyping(activeUser.id, typing);
                    }
                  }}
                  onFileUpload={(file) =>
                    handleFileUploadWrapper(activeUser.id, file)
                  }
                  onBack={handleCloseConversation}
                  onScroll={handleScroll}
                  updateMessageStatus={updateMessageStatus}
                  markAllAsRead={() => markAllAsRead(activeUser.id)}
                  clearChat={() => clearChat(activeUser.id)}
                  getOnlineUsers={getOnlineUsers}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Debug Panel (Development only) */}
      {/* {process.env.NODE_ENV === "development" && isOpen && (
        <div className="fixed bottom-4 left-4 z-[9998] bg-black/80 text-white p-3 rounded-lg text-xs max-w-xs">
          <div className="font-bold mb-1">Debug Info:</div>
          <div>Socket: {socketConnected ? "✅" : "❌"}</div>
          <div>Current User: {currentUser?.id}</div>
          <div>Active Chat: {activeUserId || "None"}</div>
          <div>Online: {onlineIds.length}</div>
          <div>Connections: {Object.keys(conversations).length}</div>
          <button
            onClick={() => {
              console.log("📊 State Dump:", {
                currentUser,
                employees,
                onlineIds,
                conversations,
                socketConnected,
                connectionError,
              });
            }}
            className="mt-2 text-blue-300 underline text-xs"
          >
            Dump State
          </button>
        </div>
      )} */}
    </>
  );
};

// Add CSS animations
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes fade-in {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes rotateSmooth {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    .animate-fade-in {
      animation: fade-in 0.3s ease-out;
    }
    
    .animate-rotateSmooth {
      animation: rotateSmooth 1.5s linear infinite;
    }
    
    /* Custom scrollbar */
    .scrollbar-hide::-webkit-scrollbar {
      width: 6px;
    }
    
    .scrollbar-hide::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    .scrollbar-hide::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;
    }
    
    .scrollbar-hide::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  `;
  document.head.appendChild(style);
}

export default React.memo(MessengerWidget);
